import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, tap } from 'rxjs';
import { RvpVersion, RvpVersionData } from '../../../rvp/models/rvp-version.model';
import { AuthService } from '../../../auth/services/auth.service';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';

@Injectable({
  providedIn: 'root',
})
export class RvpVersionService {
  private readonly http = inject(HttpClient);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly authService = inject(AuthService);

  #rvpVersionsResource = httpResource<RvpVersion[]>(() =>
    this.authService.isAuthenticated() ? `${environment.apiUrl}/rvp-versions` : undefined,
  );

  readonly rvpVersions = computed<RvpVersion[]>(() => {
    if (this.#rvpVersionsResource.status() === 'error') {
      return [];
    }
    return this.#rvpVersionsResource.value() ?? [];
  });

  readonly readyRvpVersions = computed<RvpVersion[]>(() => {
    return this.rvpVersions().filter((v) => v.state === 'ready');
  });

  #mutationLoading = signal(false);
  readonly isLoading = computed(
    () => this.#rvpVersionsResource.isLoading() || this.#mutationLoading(),
  );

  constructor() {
    effect(() => {
      if (this.#rvpVersionsResource.status() === 'error') {
        this.apiErrors.notify(
          this.#rvpVersionsResource.error(),
          'RvpVersionService.loadRvpVersions',
        );
      }
    });
  }

  /**
   * Posts the new RVP version and hands the caller the request, so a dialog that owns the action
   * can see it fail and keep the filled-in form open. The list refresh stays here; the error does not.
   */
  createRvpVersion(data: RvpVersionData): Observable<RvpVersion> {
    return this.http
      .post<RvpVersion>(`${environment.apiUrl}/rvp-versions`, data)
      .pipe(tap(() => this.#rvpVersionsResource.reload()));
  }

  /** @see createRvpVersion — the caller owns the failure. */
  updateRvpVersion(id: string, data: RvpVersionData): Observable<RvpVersion> {
    return this.http
      .put<RvpVersion>(`${environment.apiUrl}/rvp-versions/${id}`, data)
      .pipe(tap(() => this.#rvpVersionsResource.reload()));
  }

  deleteRvpVersion(id: string) {
    this.#mutationLoading.set(true);
    this.http.delete(`${environment.apiUrl}/rvp-versions/${id}`).subscribe({
      next: () => {
        this.#mutationLoading.set(false);
        this.#rvpVersionsResource.reload();
      },
      error: (error: HttpErrorResponse) => {
        this.#mutationLoading.set(false);
        this.apiErrors.notify(error, 'RvpVersionService.deleteRvpVersion');
      },
    });
  }
}
